import { ScrollingNumberCounter } from "@clipboard-health/ui-components";
import { type Theme, ThemeProvider } from "@mui/material";

const DEFAULT_DURATION_MS = 1500;
const SCORE_FONT_SIZE = "7.25rem";
const SCORE_FONT_WEIGHT = 300;
const SCORE_LINE_HEIGHT = "96px";
const SCORE_LETTER_SPACING = "-0.01em";

interface ScoreCounterProps {
  /**
   * Starting value for the counter animation.
   * When not animating, this is ignored — the counter displays `endValue` statically.
   */
  startValue: number;
  /**
   * The target value to display (or animate to).
   */
  endValue: number;
  /**
   * Whether to animate from `startValue` to `endValue`.
   * When false, the counter displays `endValue` immediately.
   */
  animationEnabled: boolean;
  /**
   * Color of the score text.
   */
  color: string;
  /**
   * Duration of the counting animation in milliseconds.
   * @default 1500
   */
  duration?: number;
}

/**
 * Animated score counter that can tick up or down between values.
 */
export function ScoreCounter(props: ScoreCounterProps) {
  const { startValue, endValue, animationEnabled, color, duration = DEFAULT_DURATION_MS } = props;

  // When not animating, set startValue === endValue to render statically
  const effectiveStartValue = animationEnabled ? startValue : endValue;

  return (
    <ThemeProvider
      theme={(outerTheme: Theme) => ({
        ...outerTheme,
        typography: {
          ...outerTheme.typography,
          h1: {
            ...outerTheme.typography.h1,
            fontSize: SCORE_FONT_SIZE,
            fontWeight: SCORE_FONT_WEIGHT,
            lineHeight: SCORE_LINE_HEIGHT,
            letterSpacing: SCORE_LETTER_SPACING,
          },
        },
      })}
    >
      <ScrollingNumberCounter
        startValue={effectiveStartValue}
        endValue={endValue}
        duration={duration}
        variant="h1"
        color={color}
      />
    </ThemeProvider>
  );
}
